import { useAppContext } from "@/context/Context";
import { useUser } from "@/context/UserContext";
import { useRouter } from "next/navigation";
import { useState } from "react";

const getThemeStyles = (isLight) => {
  const accentPurple = "#6c63ff";
  const accentLightPurple = "#9b5cff";
  const accentOrange = "#feca57";

  const lightBgCard = "rgba(255, 255, 255, 0.2)";
  const darkBgCard = "var(--color-darker)";
  const lightShadow = "0 8px 24px rgba(0, 0, 0, 0.1)";
  const darkShadow = "0 8px 24px rgba(0, 0, 0, 0.3)";

  return {
    formCard: {
      background: isLight ? lightBgCard : darkBgCard,
      backdropFilter: "blur(20px)",
      border: isLight ? "none" : "1px solid rgba(255, 255, 255, 0.1)",
      borderRadius: "24px",
      padding: "40px 35px",
      maxWidth: "420px",
      margin: "auto",
      boxShadow: isLight ? lightShadow : darkShadow,
      transition: "all 0.3s ease",
      width: "100%",
    },
    formTitle: {
      textAlign: "center",
      fontSize: "1.9rem",
      fontWeight: "700",
      color: isLight ? "#1e1e1e" : "#f2f2f2",
      marginBottom: "28px",
    },
    input: {
      width: "100%",
      padding: "12px 16px",
      borderRadius: "12px",
      fontSize: "1rem",
      border: `1px solid ${
        isLight ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.15)"
      }`,
      backgroundColor: isLight
        ? "rgba(255, 255, 255, 0.9)"
        : "var(--color-darker)",
      color: isLight ? "inherit" : "#f1f1f1",
      transition: "border-color 0.3s, box-shadow 0.3s",
    },
    btnSubmit: {
      width: "100%",
      padding: "14px",
      border: "none",
      borderRadius: "50px",
      color: "white",
      fontWeight: "600",
      fontSize: "1.05rem",
      letterSpacing: "0.4px",
      cursor: "pointer",
      transition: "all 0.3s ease",
      background: isLight
        ? `linear-gradient(90deg, ${accentPurple}, ${accentLightPurple}, ${accentOrange})`
        : `linear-gradient(90deg, ${accentLightPurple}, ${accentPurple}, #4b4bff)`,
      boxShadow: isLight
        ? "0 4px 15px rgba(108, 99, 255, 0.2)"
        : "0 0 10px rgba(155, 92, 255, 0.25)",
    },
  };
};

const ResourceForm = () => {
  const { user } = useUser();
  const router = useRouter();
  const [focusedInput, setFocusedInput] = useState(null);
  const [formData, setFormData] = useState({ name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const { isLightTheme } = useAppContext();
  const themeStyles = getThemeStyles(isLightTheme);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user) {
      // If no user, navigate to login immediately
      router.push("/login");
      return;
    }

    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
      };

      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/resourceenquiry`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json();

      if (response.ok) {
        setSuccessMessage(
          "Your enquiry has been submitted successfully! Our team will contact you soon."
        );
        setFormData({ name: "", email: "", phone: "" });
        setTimeout(() => setSuccessMessage(""), 5000);
      } else {
        setErrorMessage(
          data.error ||
            data.message ||
            "Failed to submit enquiry. Please try again."
        );
        setTimeout(() => setErrorMessage(""), 5000);
      }
    } catch (error) {
      setErrorMessage("Something went wrong. Please try again later.");
      setTimeout(() => setErrorMessage(""), 5000);
    } finally {
      setLoading(false);
    }
  };

  const getInputStyle = (inputName) => {
    const style = { ...themeStyles.input };
    if (focusedInput === inputName) {
      style.borderColor = "#6c63ff";
      style.boxShadow = "0 0 10px rgba(108, 99, 255, 0.3)";
    }
    return style;
  };

  return (
    <div style={themeStyles.formCard}>
      <h3 style={themeStyles.formTitle}>Sign Up for Free Resources</h3>

      {successMessage && (
        <div className="alert alert-success mb-3">{successMessage}</div>
      )}
      {errorMessage && (
        <div className="alert alert-danger mb-3">{errorMessage}</div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Name Field */}
        <div style={{ marginBottom: "18px" }}>
          <input
            name="name"
            type="text"
            placeholder="Name"
            value={formData.name}
            onChange={handleChange}
            onFocus={() => setFocusedInput("name")}
            onBlur={() => setFocusedInput(null)}
            style={getInputStyle("name")}
          />
        </div>

        {/* Email Field */}
        <div style={{ marginBottom: "18px" }}>
          <input
            name="email"
            placeholder="Email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            onFocus={() => setFocusedInput("email")}
            onBlur={() => setFocusedInput(null)}
            style={getInputStyle("email")}
          />
        </div>

        {/* Phone Field */}
        <div style={{ marginBottom: "18px" }}>
          <input
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            onFocus={() => setFocusedInput("phone")}
            onBlur={() => setFocusedInput(null)}
            placeholder="Phone"
            style={getInputStyle("phone")}
          />
        </div>

        <div>
          <button
            type="submit"
            style={themeStyles.btnSubmit}
            disabled={loading}
          >
            {loading
              ? "SUBMITTING..."
              : !user
              ? "LOGIN/REGISTER →"
              : "GET IT NOW →"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ResourceForm;
